import {
  createRouter,
  createWebHistory,
  type RouteRecordRaw,
  type RouteLocationNormalized,
} from "vue-router";
import { useAuthStore } from "@/stores/auth";
import LoginView from "@/views/LoginView.vue";

type RouteMeta = {
  title?: string;
  requiresAuth?: boolean;
  isAuthCallback?: boolean;
}

const routes: RouteRecordRaw[] = [
  {
    path: "/",
    name: "login",
    component: LoginView,
  },
  {
    path: "/ask",
    name: "ask",
    component: () => import("@/views/AskView.vue"),
    meta: {
      requiresAuth: true
    },
  },
  {
    path: "/bookmarks",
    name: "bookmarks",
    component: () => import("@/views/BookmarksView.vue"),
    meta: {
      title: "Bookmarks",
    },
  },
  {
    path: "/documents",
    name: "documents",
    component: () => import("@/views/DocumentsView.vue"),
    meta: {
      title: "Documents",
    }
  },
  {
    path: "/documents/:uuid",
    name: "documents-show",
    component: () => import("@/views/DocumentsShowView.vue"),
  },
  {
    path: '/help',
    name: 'help',
    component: () => import('@/views/HelpView.vue'),
  },
  {
    path: "/auth/callback",
    name: "auth-callback",
    component: () => import("@/views/AuthCallbackView.vue"),
    meta: { isAuthCallback: true },
  },
];

const DEFAULT_PAGE_TITLE = "Optix";

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

router.beforeEach(async (to: RouteLocationNormalized) => {
  const authStore = useAuthStore();
  const meta: RouteMeta = to.meta;

  document.title = meta.title || DEFAULT_PAGE_TITLE;

  // ✅ Let callback route render immediately
  if (meta.isAuthCallback) {
    return true;
  }

  // ✅ Ensure auth is initialised
  if (!authStore.ready) {
    await authStore.bootstrap();
  }

  // ✅ Protect routes
  if (
      meta.requiresAuth &&
      !authStore.isAuthenticated &&
      !authStore.interactionInProgress
  ) {
    await authStore.signIn(to.fullPath);
    return false;
  }

  return true;
});

export default router;